import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { createPageUrl } from "../utils";
import { ArrowLeft, X } from "lucide-react";
import Button from "../components/ui/Button";
import Footer from "../components/home/Footer";

import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

import chanel from "../img/products/manhole/chanel.jpg";
import cover from "../img/products/manhole/cover.jpg";
import recessed from "../img/products/manhole/recessed.jpg";
import round from "../img/products/manhole/round.jpg";
import seal from "../img/products/manhole/seal.jpg";

/* -----------------------------------
   PRODUCT DATA
----------------------------------- */

const products = [
  {
    name: "Channel / Gully Grating",
    category: "Ductile Iron",
    description:
      "Heavy-duty channel and gully grating designed for efficient surface water drainage, offering high load-bearing capacity, anti-skid surface and long-term durability for urban and industrial applications.",
    image: chanel,
  },
  {
    name: "Carriageway Cover & Frame",
    category: "Ductile Iron – Heavy Duty",
    description:
      "Robust carriageway cover and frame system engineered for high-traffic roads, ensuring excellent load resistance, stability, and safety in demanding infrastructure environments.",
    image: cover,
  },
  {
    name: "Solid Top With Single / Double Seal",
    category: "Light, Medium & Heavy Duty",
    description:
      "Solid top manhole covers with single or double sealing options for effective odour control, water tightness, and superior performance across various load classes.",
    image: seal,
  },
  {
    name: "Round Cover & Square Frame",
    category: "Medium / Heavy Duty",
    description:
      "Round ductile iron cover paired with square frame, providing secure seating, anti-rock design and high strength for municipal and industrial installations.",
    image: round,
  },
  {
    name: "Recessed Cover & Frame",
    category: "Single Seal / Double Seal",
    description:
      "Recessed cover and frame system suitable for block paving or screed finishes, offering flush installation, sealing options and reliable structural performance.",
    image: recessed,
  },
];

/* -----------------------------------
   COMPONENT
----------------------------------- */

const ManholeCoverProducts = () => {
  const [previewImage, setPreviewImage] = React.useState(null);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ================= HEADER ================= */}

      <div className="bg-gradient-to-br from-[#0d1442] via-[#1a237e] to-[#0d1442] text-white py-20">
        <div className="max-w-7xl mx-auto px-6">

          <Link to={createPageUrl("Home")}>
            <Button
              variant="ghost"
              className="text-white hover:text-[#f9d71c] mb-8 -ml-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="h-1 w-16 bg-[#f9d71c]" />
              <span className="text-[#f9d71c] font-semibold tracking-wider uppercase text-sm">
                Products
              </span>
            </div>

            <h1 className="text-4xl md:text-6xl font-light mb-4">
              Manhole Covers <span className="font-semibold">& Gratings</span>
            </h1>

            <p className="text-white/90 text-lg max-w-3xl leading-relaxed">
              Premium EN 124 compliant ductile iron manhole covers and gratings
              engineered for superior load-bearing capacity and long service
              life.
            </p>
          </motion.div>

        </div>
      </div>

      {/* ================= PRODUCTS ================= */}

      <div className="max-w-7xl mx-auto px-6 py-20">

        {products.map((product, index) => (

          <motion.div
            key={product.name}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="relative overflow-hidden min-h-[700px] grid md:grid-cols-2 gap-12 items-start py-24 border-b border-gray-200"
          >

            {/* LEFT - TEXT */}
            <div className="pt-6">

              <span className="text-[#d4a106] text-sm uppercase tracking-wider font-medium block mb-3">
                {product.category}
              </span>

              <h3 className="text-4xl md:text-5xl font-semibold text-[#1a237e] mb-6 leading-tight">
                {product.name}
              </h3>

              <p className="text-lg md:text-xl text-gray-600 max-w-xl leading-relaxed">
                {product.description}
              </p>

              {/* Accent Line */}
              <div className="w-24 h-[2px] bg-[#f9d71c] mt-8" />

            </div>

            {/* RIGHT - IMAGE */}
            <div className="relative group">

              <img
                src={product.image}
                alt={product.name}
                onClick={() => setPreviewImage(product.image)}
                className="w-full h-[520px] object-contain cursor-zoom-in
                           transition-all duration-300
                           group-hover:scale-[1.02]
                           group-hover:drop-shadow-[0_20px_30px_rgba(0,0,0,0.15)]"
              />

              {/* Overlay Hint */}
              <div
                className="absolute inset-0 flex items-center justify-center
                           opacity-0 group-hover:opacity-100
                           transition-opacity
                           pointer-events-none"
              >
                <span className="bg-black/60 text-white px-4 py-2 rounded-full text-sm">
                  Click to Preview
                </span>
              </div>

            </div>

          </motion.div>

        ))}

      </div>

      {/* ================= IMAGE PREVIEW MODAL ================= */}

      <AnimatePresence>
        {previewImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"
            onClick={() => setPreviewImage(null)}
          >

            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="relative w-full h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >

              {/* CLOSE */}
              <button
                className="absolute top-6 right-6 text-white hover:text-gray-300 z-10"
                onClick={() => setPreviewImage(null)}
              >
                <X size={32} />
              </button>

              <TransformWrapper
                initialScale={1}
                minScale={1}
                maxScale={4}
                wheel={{ step: 0.2 }}
                doubleClick={{ disabled: true }}
              >
                <TransformComponent
                  wrapperClass="w-full h-full flex items-center justify-center"
                  contentClass="flex items-center justify-center"
                >
                  <img
                    src={previewImage}
                    alt="Preview"
                    className="max-h-[85vh] max-w-[90vw] object-contain bg-white p-4 rounded-lg"
                  />
                </TransformComponent>
              </TransformWrapper>

            </motion.div>

          </motion.div>
        )}
      </AnimatePresence>

      {/* ================= CTA ================= */}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto px-6 pb-24"
      >

        <div className="bg-[#1a237e] rounded-3xl p-10 md:p-14 text-center">

          <h3 className="text-2xl md:text-3xl font-light text-white mb-4">
            Need Custom{" "}
            <span className="font-medium text-[#f9d71c]">Specifications?</span>
          </h3>

          <p className="text-white/70 max-w-2xl mx-auto mb-8">
            All products can be customized in size, load class, and coating as
            per your project requirements.
          </p>

          <Link to="/#contact">
            <Button className="bg-[#f9d71c] text-[#1a237e] hover:bg-[#f9d71c]/90 px-10 py-6 text-lg font-medium rounded-full">
              Contact Sales Team
            </Button>
          </Link>

        </div>

      </motion.div>

      <Footer />

    </div>
  );
};

export default ManholeCoverProducts;
